import React, { FC, useMemo } from 'react';
import { FaBullhorn, FaCreditCard, FaRegFileAlt } from 'react-icons/fa';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { Button, TabItem, Tabs } from '@/lib';
import { RouteConstants } from '@/features/router/RouteConstants';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useGetCampaignsCountV2Query } from '@/services/endpoints/campaigns';
import { useEmailTemplates } from '../hooks/useEmailTemplates';
import { CampaignsEmailRoutesV2 } from './CampaignsEmailRoutesV2';
import { EMAIL_CONNECTOR_NAME } from '../constants';
import { useGetWorkspaceConnectorsQuery } from '@/services/endpoints/workspace-connectors';
import { useCompanyValues } from '@/providers';
import { useTranslation } from 'react-i18next';
import { Loading } from '@/components/loaders';

const EMAIL_CAMPAIGNS_PATTERNS = [
  RouteConstants.emailCampaigns,
  RouteConstants.createCampaignEmail,
  RouteConstants.editCampaignEmail,
  RouteConstants.emailCampaignDetails,
];

const EMAIL_TEMPLATES_PATTERNS = [
  RouteConstants.emailTemplates,
  RouteConstants.createEmailTemplate,
  RouteConstants.editEmailTemplate,
  RouteConstants.emailTemplateDetails,
  RouteConstants.emailTemplatesEditHub,
  RouteConstants.emailTemplatesDeleteHub,
];

const EMAIL_PACKAGES_PATTERNS = [RouteConstants.emailPackages];

const ConnectorNotConnectedBlock: FC<{ type: 'email' }> = ({ type }) => {
  const history = useHistory();
  const { t } = useTranslation('campaign');

  const handleConnect = () => {
    if (type === 'email') {
      history.push(RouteConstants.destinations);
    }
  };

  return (
    <div className='flex flex-col items-center justify-center gap-6 rounded-lg border border-gray-400 py-20'>
      <h2 className='text-4xl font-semibold text-slate-600'>{t('whatsappWorkspace.noWhatsappConnectorTitle', { connectorName: EMAIL_CONNECTOR_NAME })}</h2>
      <div className='flex flex-col items-center gap-4'>{t('whatsappWorkspace.noWhatsappConnectorDescription', { connectorName: EMAIL_CONNECTOR_NAME })}</div>
      <FaRegFileAlt className='size-12 text-gray-400' />
      <Button onClick={handleConnect}>{t('goToDestinationsText')}</Button>
    </div>
  );
};

const EmailWorkspaceInit = () => {
  const history = useHistory();
  const location = useLocation();
  const workspace = useWorkspace();

  const { data: campaignsCount } = useGetCampaignsCountV2Query({ workspaceId: workspace.id, providerType: 'EMAIL' });
  const { templatesCount, isLoading: isTemplatesLoading } = useEmailTemplates();

  const tabItems = useMemo<TabItem[]>(
    () => [
      {
        id: 'campaigns',
        title: 'campaign:whatsappWorkspace.campaigns' as const,
        icon: <FaBullhorn className='size-4' />,
        count: campaignsCount ?? 'N/A',
        element: <CampaignsEmailRoutesV2 />,
      },
      {
        id: 'templates',
        title: 'campaign:whatsappWorkspace.templates' as const,
        icon: <FaRegFileAlt className='size-4' />,
        count: isTemplatesLoading ? 'N/A' : templatesCount,
        element: <CampaignsEmailRoutesV2 />,
      },
      {
        id: 'packages',
        title: 'campaign:whatsappWorkspace.packages' as const,
        icon: <FaCreditCard className='size-4' />,
        count: 4,
        element: <CampaignsEmailRoutesV2 />,
      },
    ],
    [campaignsCount, isTemplatesLoading, templatesCount]
  );

  const activeTabId = useMemo(() => {
    const isCampaignRoute = EMAIL_CAMPAIGNS_PATTERNS.some((pattern) => matchPath(location.pathname, { path: pattern, exact: true }));
    if (isCampaignRoute) {
      return 'campaigns';
    }

    const isTemplateRoute = EMAIL_TEMPLATES_PATTERNS.some((pattern) => matchPath(location.pathname, { path: pattern, exact: true }));
    if (isTemplateRoute) {
      return 'templates';
    }

    const isPackageRoute = EMAIL_PACKAGES_PATTERNS.some((pattern) => matchPath(location.pathname, { path: pattern, exact: true }));
    if (isPackageRoute) {
      return 'packages';
    }

    return 'campaigns';
  }, [location.pathname]);

  const currentTab = tabItems.find((item) => item.id === activeTabId) ?? tabItems[0];

  const handleSelectTab = (tabItem: TabItem) => {
    if (tabItem.id === 'campaigns') {
      history.push(RouteConstants.emailCampaigns);
      return;
    }

    if (tabItem.id === 'templates') {
      history.push(RouteConstants.emailTemplates);
      return;
    }

    if (tabItem.id === 'packages') {
      history.push(RouteConstants.emailPackages);
    }
  };

  return <Tabs items={tabItems} currentTab={currentTab.title} onSelectTab={handleSelectTab} />;
};

export const CampaignsEmailWorkspaceV2: FC = () => {
  const workspace = useWorkspace();
  const { currentLanguage } = useCompanyValues();

  const { data: customConnectors, isLoading: isConnectorsLoading } = useGetWorkspaceConnectorsQuery(
    { workspaceId: workspace.id, lang: currentLanguage },
    { skip: !workspace.id }
  );

  const isEmailAuthenticated = customConnectors?.find((connector) => connector.name === EMAIL_CONNECTOR_NAME)?.isAuthenticated ?? false;

  if (isConnectorsLoading) {
    return <Loading />;
  }

  if (!isEmailAuthenticated) {
    return <ConnectorNotConnectedBlock type='email' />;
  }

  return <EmailWorkspaceInit />;
};
